/**
 * WhatsApp-Kontakt: Nummer lesen (öffentlich) und pflegen (Administration).
 * Ablage in `app_settings`, siehe src/lib/whatsapp.ts.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { FEATURE_FLAGS } from "@/lib/feature-flags.keys";
import {
  normalizeWhatsAppNumber,
  WHATSAPP_SETTING_KEY,
  WHATSAPP_SETTING_LABEL,
} from "@/lib/whatsapp";

type Ctx = {
  supabase: { rpc: (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown }> };
  userId: string;
};

async function hasRole(ctx: Ctx, role: "admin" | "editor"): Promise<boolean> {
  const { data } = await ctx.supabase.rpc("has_role", { _user_id: ctx.userId, _role: role });
  return Boolean(data);
}

async function readNumber(): Promise<{ number: string | null; updatedAt: string | null }> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("app_settings")
    .select("value, updated_at")
    .eq("key", WHATSAPP_SETTING_KEY)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return {
    number: normalizeWhatsAppNumber(data?.value ?? null),
    updatedAt: (data?.updated_at as string | null) ?? null,
  };
}

/** Schalter auf der Seite Feature-Flags. Fehlender Eintrag = eingeschaltet. */
async function isButtonEnabled(): Promise<boolean> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("feature_flags")
    .select("enabled")
    .eq("key", FEATURE_FLAGS.whatsappButtonVisible)
    .maybeSingle();
  return data ? data.enabled === true : true;
}

/**
 * Öffentlich: nur die Nummer in wa.me-Form, sonst nichts. Null, wenn keine
 * Nummer eingetragen oder der Schalter aus ist.
 */
export const getWhatsAppContact = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ number: string | null }> => {
    const [{ number }, enabled] = await Promise.all([readNumber(), isButtonEnabled()]);
    return { number: enabled ? number : null };
  },
);

/** Admin-Seite: aktueller Stand. Lesen dürfen interne Konten. */
export const getWhatsAppSetting = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const ctx = context as Ctx;
    const [isAdmin, isEditor] = await Promise.all([hasRole(ctx, "admin"), hasRole(ctx, "editor")]);
    if (!isAdmin && !isEditor) throw new Error("Forbidden: Interner Zugang erforderlich");
    return { ...(await readNumber()), canEdit: isAdmin };
  });

/** Nummer setzen oder leeren (leer = Button ausgeblendet). Nur Administration. */
export const setWhatsAppSetting = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ number: z.string().trim().max(40) }).parse(d))
  .handler(async ({ data, context }) => {
    const ctx = context as Ctx;
    if (!(await hasRole(ctx, "admin"))) {
      throw new Error("Forbidden: Administratorrechte erforderlich");
    }
    const normalized = data.number === "" ? "" : normalizeWhatsAppNumber(data.number);
    if (normalized === null) {
      throw new Error(
        "Bitte eine gültige Mobilnummer mit Ländervorwahl eingeben, z. B. +49 170 1234567.",
      );
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("app_settings").upsert(
      {
        key: WHATSAPP_SETTING_KEY,
        value: normalized,
        label: WHATSAPP_SETTING_LABEL,
        description:
          "Ziel des schwebenden WhatsApp-Buttons auf der Website. Leer = Button ausgeblendet.",
        updated_at: new Date().toISOString(),
        updated_by: ctx.userId,
      },
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);

    await (
      await import("@/lib/audit.server")
    ).writeAuditEvent({
      action: "APP_SETTING_UPDATE",
      tableName: "app_settings",
      recordId: WHATSAPP_SETTING_KEY,
      newData: { value: normalized },
      context: "admin/whatsapp",
      actorId: ctx.userId,
    });
    return { number: normalized || null };
  });
